import { useEffect } from "react";
import { PLATES } from "../../plate/palettes";
import LegendGlyph from "./LegendGlyph";

export const REV = "2026-10-10";

const LEGEND_ROWS = [
  ["method", "METHOD"], ["venture", "VENTURE"], ["sys", "SYSTEM"], ["course", "COURSE"],
  ["silicon", "SILICON"], ["hardware", "HARDWARE"], ["flight", "FLIGHT"], ["pub", "PUBLICATION"],
  ["cv", "CURRICULUM VITAE"], ["dist", "DISTRIBUTION"],
  ["priv", "CLOSED SOURCE"], ["assembly", "OPENS FURTHER"],
  ["solid", "INTERFACE"], ["dash", "PRODUCED BY"], ["offshoot", "OFFSHOOT"],
  ["opens", "OPENS — +n"], ["leaves", "LINK OUT — ↗"], ["contain", "CONTAINS"],
  ["query", "CUT UNRESOLVED"], ["unknown", "PLACEMENT UNRESOLVED"],
];

const INDEX_ROWS = [
  ["SILICON", "1", "custom ASIC — Verilog, submitted for fabrication"],
  ["HARDWARE", "1", "quadruped — ROS 2, wireless locomotion"],
  ["FLIGHT SW", "2", "F′ component architecture, telemetry"],
  ["AGENT INFRA", "9", "MCP servers, skill engine, registry, observatory"],
  ["VENTURE", "1", "Human Centered Systems — R&D for complex systems"],
  ["COURSEWORK", "7", "study hub + per-class agent scaffold, fall 2026"],
  ["METHOD", "1", "IAKM — DOI-versioned, v2"],
];

const PLATE_BUTTONS = [["drawing", "DRAWING"], ["instrument", "INSTRUMENT"], ["plate", "PLATE"]];

/**
 * The flat drawing furniture laid over the 3D model: border, scale note, plate
 * switcher, legend, index and title block.
 *
 * counts: { shown, folded, unplaced, queries }
 * children: rendered at the top of the left column (the unplaced block).
 * credentialsHref: optional; the cross-reference is only rendered when it has
 * somewhere real to go.
 */
export default function SheetFurniture({
  palette = PLATES.drawing, plateKey = "drawing", onPlateChange, counts = {}, credentialsHref,
  onReset, children,
}) {
  // Publish the active plate as CSS variables so every sheet component (and the
  // detail panel / unplaced block) recolours together.
  useEffect(() => {
    const root = document.documentElement.style;
    const vars = {
      "--plate-bg": palette.bg, "--plate-ink": palette.ink, "--plate-line": palette.line,
      "--plate-faint": palette.faint, "--plate-mute": palette.mute, "--plate-flag": palette.flag,
      "--plate-ok": palette.ok, "--plate-live": palette.live,
    };
    Object.entries(vars).forEach(([k, v]) => root.setProperty(k, v));
    return () => Object.keys(vars).forEach((k) => root.removeProperty(k));
  }, [palette]);

  const { shown = "—", folded = "—", unplaced = 0, queries = 0 } = counts;

  return (
    <div className="plate-sheet">
      <div className="sheet-border" aria-hidden="true" />
      <div className="sheet-border inner" aria-hidden="true" />

      <p className="sheet-scale">SCALE 1:1 · ORTHOGRAPHIC</p>

      <fieldset className="sheet-plates">
        <legend className="sr-only">Plate style</legend>
        {PLATE_BUTTONS.map(([key, label]) => (
          <button key={key} type="button" aria-pressed={plateKey === key}
                  onClick={() => onPlateChange && onPlateChange(key)}>{label}</button>
        ))}
      </fieldset>

      {/* Outside the fieldset: that group is the plate style, and this is not
          one of its choices. Rendered only when there is a drawing to reset. */}
      {onReset ? (
        <button type="button" className="sheet-reset" onClick={onReset}>
          RESET VIEW
        </button>
      ) : null}

      {/* One flex column for everything on the left edge: slot content (the
          unplaced block), the credentials cross-reference, then the legend.
          They share a single flow, so they cannot overlap at any height. */}
      <div className="sheet-left">
        {children}

        {credentialsHref ? (
          <a className="sheet-cref" href={credentialsHref}>SEE SHEET C-1 — CREDENTIALS →</a>
        ) : null}

        <section className="plate-panel sheet-legend" aria-labelledby="plate-legend-h">
          <h2 className="p-label" id="plate-legend-h">LEGEND</h2>
          <ul>
            {LEGEND_ROWS.map(([kind, text]) => (
              <li key={kind} className="lrow">
                <LegendGlyph kind={kind} palette={palette} />
                <span className="rowv">{text}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <section className="plate-panel sheet-index" aria-labelledby="plate-index-h">
        <h2 className="p-label" id="plate-index-h">INDEX</h2>
        <table>
          <tbody>
            {INDEX_ROWS.map(([name, n, text]) => (
              <tr key={name} className="irow">
                <th scope="row" className="rowk">{name}</th>
                <td className="rowk n">{n}</td>
                <td className="rowv">{text}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="plate-panel sheet-titleblock" aria-label="Title block">
        <h1 className="tb-name">LIZ OSBORN</h1>
        <p className="tb-sub">SYSTEM DECOMPOSITION — SELF</p>
        <div className="tb-grid">
          <p className="tb-cell">SHEET &nbsp;<b>S-1</b></p>
          <p className="tb-cell">REV &nbsp;<b>{REV}</b></p>
          <p className="tb-cell">SHOWN <b>{shown}</b> &nbsp; FOLDED <b>{folded}</b></p>
          <p className="tb-cell flag">QUERIES {queries} · UNPLACED {unplaced}</p>
        </div>
      </section>
    </div>
  );
}
